import { useState, useCallback } from 'react'
import { BookingCustomField, BookingCustomFieldType } from '@xeetr/types/models'

export interface UseCustomFieldEditingOptions {
  field: BookingCustomField
  onSave: (updatedField: BookingCustomField) => void
  onCancel: () => void
  onDelete: () => void
}

export interface UseCustomFieldEditingReturn {
  // Current editing values
  editingFieldName: string
  editingFieldDescription: string
  editingFieldRequired: boolean
  editingFieldHidden: boolean
  editingFieldCustomerVisible: boolean
  editingFieldType: BookingCustomFieldType
  editingFieldOptions: string

  // Setters
  setEditingFieldName: (value: string) => void
  setEditingFieldDescription: (value: string) => void
  setEditingFieldRequired: (value: boolean) => void
  setEditingFieldHidden: (value: boolean) => void
  setEditingFieldCustomerVisible: (value: boolean) => void
  setEditingFieldType: (value: BookingCustomFieldType) => void
  setEditingFieldOptions: (value: string) => void

  // Actions
  handleEdit: () => void
  handleSave: () => void
  handleCancel: () => void
  handleDelete: () => void

  // Validation
  canSave: boolean
}

export const useCustomFieldEditing = ({
  field,
  onSave,
  onCancel,
  onDelete,
}: UseCustomFieldEditingOptions): UseCustomFieldEditingReturn => {
  // Local editing state
  const [editingFieldName, setEditingFieldName] = useState('')
  const [editingFieldDescription, setEditingFieldDescription] = useState('')
  const [editingFieldRequired, setEditingFieldRequired] = useState(false)
  const [editingFieldHidden, setEditingFieldHidden] = useState(false)
  const [editingFieldType, setEditingFieldType] = useState<BookingCustomFieldType>('text')
  const [editingFieldOptions, setEditingFieldOptions] = useState('')
  const [editingFieldCustomerVisible, setEditingFieldCustomerVisible] = useState(false)

  // Initialize editing state when editing starts
  const handleEdit = useCallback(() => {
    setEditingFieldName(field.name)
    setEditingFieldDescription(field.description)
    setEditingFieldRequired(field.required)
    setEditingFieldHidden(field.hidden || false)
    setEditingFieldType(field.type || 'text')
    setEditingFieldOptions(field.options?.join(', ') || '')
    setEditingFieldCustomerVisible(field.customerVisible || false)
  }, [field])

  // Validation for save
  const canSave =
    editingFieldName.trim() !== '' &&
    editingFieldDescription.trim() !== '' &&
    (editingFieldType !== 'enum' || editingFieldOptions.trim() !== '')

  const handleSave = useCallback(() => {
    if (!canSave) return

    const updatedField: BookingCustomField = {
      ...field,
      name: editingFieldName.trim(),
      description: editingFieldDescription.trim(),
      required: editingFieldRequired,
      hidden: editingFieldHidden,
      type: editingFieldType,
      options:
        editingFieldType === 'enum'
          ? editingFieldOptions
              .split(',')
              .map((opt: string) => opt.trim())
              .filter((opt: string) => opt)
          : undefined,
      customerVisible: editingFieldCustomerVisible,
    }

    onSave(updatedField)
  }, [
    editingFieldName,
    editingFieldDescription,
    editingFieldRequired,
    editingFieldType,
    editingFieldOptions,
    editingFieldCustomerVisible,
    canSave,
    onSave,
    field,
  ])

  const handleCancel = useCallback(() => {
    onCancel()
  }, [onCancel])

  const handleDelete = useCallback(() => {
    onDelete()
  }, [onDelete])

  return {
    // Current values
    editingFieldName,
    editingFieldDescription,
    editingFieldRequired,
    editingFieldHidden,
    editingFieldType,
    editingFieldOptions,
    editingFieldCustomerVisible,

    // Setters
    setEditingFieldName,
    setEditingFieldDescription,
    setEditingFieldRequired,
    setEditingFieldHidden,
    setEditingFieldType,
    setEditingFieldOptions,
    setEditingFieldCustomerVisible,

    // Actions
    handleEdit,
    handleSave,
    handleCancel,
    handleDelete,

    // Validation
    canSave,
  }
}
